import { Component, OnInit } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { ActivatedRoute, Router } from '@angular/router'
import moment from 'moment'
import { forkJoin } from 'rxjs'
import { ClientInformationAttribute } from 'src/app/models/client-information/ClientInformationAttribute'
import { ClientDto } from 'src/app/models/client/client-dto'
import { Filter } from 'src/app/models/power-bi/filter'
import { SlatHistoryDto } from 'src/app/models/slat/slat-history-dto'
import { SlatHistoryTableDTO } from 'src/app/models/slat/slat-history-table-dto'
import { SlatHistoryTableColumnDefinitions } from 'src/app/models/table-defs/slat-history-table-def'
import { TableColumnDefinition } from 'src/app/models/table/table-column-definition'
import { TableLinkColumnDTO } from 'src/app/models/table/table-link-column-dto'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ClientService } from 'src/app/services/client/client.service'
import { CommonService } from 'src/app/services/common/common.service'
import { FeaturesService } from 'src/app/services/features/features.service'
import { PreviousRouteService } from 'src/app/services/previous-route/previous-route.service'
import { SlatService } from 'src/app/services/slat/slat.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-slat-history',
  imports: [],
  templateUrl: './slat-history.component.html',
  styleUrl: './slat-history.component.scss',
  standalone: false,
})
export class SlatHistoryComponent implements OnInit {
  _clientId: string
  _title = ''
  _age: string = ''
  _dob: Date
  _dateFormat: string = 'Do MMMM, YYYY'
  _fallbackUrl = '/dashboard'
  _reddiCreateReportUrl: string

  _showClientDetails: boolean = false
  _clientDetails: ClientDto
  _slatFilters: Filter[]
  _tableData: MatTableDataSource<SlatHistoryTableDTO> =
    new MatTableDataSource<SlatHistoryTableDTO>()
  _tableColumnsToDisplay: string[] = [
    'template',
    'presentationDate',
    'age',
    'presenterName',
    'assessmentStatus',
    'notes',
    'editReport',
    'viewReport',
  ]
  _tableColumnDefinitions: TableColumnDefinition[] =
    SlatHistoryTableColumnDefinitions

  _slatHistory: SlatHistoryDto[] = []
  //Client Attributes
  _clientInformationAttributes: ClientInformationAttribute[] =
    this.commonService.createEmptyAttributeList()
  _clientInformationTitle = 'Client Information'
  _clientInformationDisplayText = ''
  _clientInformationUrl = ''
  _clientInFocus: string = ''
  _clientEditEnabled: boolean = false //Perform check based on Feature Flag
  _totalReviews = 0
  _hasAccess: any = {
    slatHasAccess: false,
    flipHasAccess: false,
    reddiHasAccess: false,
  }

  constructor(
    private route: ActivatedRoute,
    private bannerService: BannerService,
    protected spinnerService: SpinnerService,
    private clientService: ClientService,
    public slatService: SlatService,
    private previousRouteService: PreviousRouteService,
    public router: Router,
    private commonService: CommonService,
    public featureService: FeaturesService,
    public translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.initialiseForm()

    this.translateService.onLangChange.subscribe(() => {
      this.initialiseForm()
    })
  }

  initialiseForm() {
    this.spinnerService.show()

    this._clientId = this.route.snapshot.paramMap.get('clientId')
    if (!this.commonService.isValidUUID(this._clientId)) {
      this.bannerService.showBanner(`Invalid client requested.`, 'error', 3000)
      this.previousRouteService.goToPreviousUrl(this._fallbackUrl)
    }

    this._slatFilters = [
      {
        attribute: 'Client/contactid',
        operation: 'eq',
        value: this._clientId,
      },
    ]

    this._reddiCreateReportUrl = '/slat/slat-report-form/' + this._clientId
    let requests = {
      clientDetails: this.clientService.Details(this._clientId),
      slatHistory: this.slatService.History(this._clientId),
      features: this.featureService.GetFeatures(),
    }

    forkJoin(requests).subscribe({
      next: ({ clientDetails, slatHistory, features }) => {
        this._clientEditEnabled = this.commonService.getFeatureValue(
          features,
          'clientEdit',
        )
        this._clientDetails = clientDetails
        this._age = this._clientDetails.age
        this._dob = this._clientDetails.dateOfBirth
        this._title = this._clientDetails.preferredFullName
        this._showClientDetails = false
        this.setClientInformation(clientDetails)
        this._slatHistory = slatHistory
        //tools has access check
        this.checkToolsAccess()
        this.retriveIndicators(clientDetails)

        this._tableData = this.MapHistoryToTable(
          this._clientId,
          this._slatHistory,
        )
        this.spinnerService.hide()
      },
      error: () => {
        this.bannerService.showBanner(
          `Unable to retrieve details.`,
          'error',
          3000,
        )

        this.spinnerService.hide()
      },
    })
  }

  routerLinkClicked() {
    this.router.navigate([`/client-dashboard/${this._clientId}`])
  }

  MapHistoryToTable(
    clientId: string,
    history: SlatHistoryDto[],
  ): MatTableDataSource<SlatHistoryTableDTO> {
    const tableData: SlatHistoryTableDTO[] = new Array<SlatHistoryTableDTO>()
    for (let i = 0; i < history.length; i++) {
      let source = history[i]

      let target = new SlatHistoryTableDTO()
      target.template = source.templateAbbreviatedName
      target.age = source.ageInMonthsAtAssessment
      target.notes = source.notes
      target.assessmentStatus = this.commonService.translateSafe(
        source.assessmentStatus,
      )
      target.presentationDate = moment(source.assessmentDate).format(
        'DD-MMM-YYYY',
      )
      target.presenterName = source.presenterName
      target.impactingFactors = source.impactingFactors
      target.slatId = source.instanceId
      target.clientId = clientId
      target.editReport = new TableLinkColumnDTO()
      target.editReport.linkText = 'Edit details'
      target.editReport.linkUrl = `/slat/assessment/${this._clientId}/${source.instanceId}`
      target.viewReport = new TableLinkColumnDTO()
      target.viewReport.linkText = 'View details'
      target.viewReport.linkUrl = `/slat/instance/${this._clientId}/${source.instanceId}`

      tableData.push(target)
    }

    return new MatTableDataSource<SlatHistoryTableDTO>(tableData)
  }

  navigateTo(target: string): void {
    this.commonService.navigateTo(target, this._clientId)
  }

  clientHasAccessToFlip(client: ClientDto): boolean {
    return this.commonService.clientHasAccessToFlip(client)
  }

  checkToolsAccess() {
    this._hasAccess.flipHasAccess = this.commonService.clientHasAccessToFlip(
      this._clientDetails,
    )
    this._hasAccess.reddiHasAccess = this.commonService.clientHasAccessToReddi(
      this._clientDetails,
    )
    this._hasAccess.slatHasAccess = this.commonService.clientHasAccessToSlat(
      this._clientDetails,
    )
  }

  retriveIndicators(client: ClientDto) {
    this._totalReviews = this.commonService.returnReviewIndicator(client)
  }

  setClientInformation(client: ClientDto) {
    this._clientInFocus = client.contactId
    this._clientInformationDisplayText = this._clientEditEnabled
      ? 'Edit profile'
      : ''
    this._clientInformationUrl = this._clientEditEnabled
      ? '/edit-client/' + client.contactId
      : ''

    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Age',
      client.age,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'DOB',
      moment(client.dateOfBirth).format('Do MMMM, YYYY'),
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Gender',
      client.genderCodeName,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Case Manager',
      client.clientCaseManagerName,
    )
  }

  previousRoute() {
    const prevUrl = this.previousRouteService.getPreviousUrl()
    if (prevUrl) {
      this.previousRouteService.goToPreviousUrl(prevUrl)
    }
  }
}
